import { useEffect, useMemo, useState } from 'react';

import { SECURE_API } from 'src/api/api';
import { isFiniteLatLng } from 'src/helpers/checkinMapBounds';
import {
  competitionIdsFromEditionCompetitions,
  getCompetitionMarkerColor,
} from 'src/helpers/competitionMarkerColors';
import { ICompetition } from 'src/interfaces/Competition';
import { IAnonymizedCheckin } from 'src/interfaces/Checkin';

interface EditionMapMarker {
  id: string;
  lat: number;
  lng: number;
  competitionId: string;
  color: string;
}

interface EditionMapState {
  markers: EditionMapMarker[];
  isLoading: boolean;
  error: string | null;
  totalCheckins: number;
  missingLocationCount: number;
}

export function useEditionCheckinMap(
  editionId: string | undefined,
  competitions: ICompetition[],
): EditionMapState {
  const [checkins, setCheckins] = useState<IAnonymizedCheckin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const competitionIdsInOrder = useMemo(
    () => competitionIdsFromEditionCompetitions(competitions),
    [competitions],
  );

  useEffect(() => {
    if (!editionId) {
      setCheckins([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    SECURE_API.get<IAnonymizedCheckin[]>(`checkins/results/${editionId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setCheckins(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCheckins([]);
          setError('fetch');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [editionId]);

  const { markers, missingLocationCount } = useMemo(() => {
    const built: EditionMapMarker[] = [];
    let missing = 0;

    checkins.forEach((row, index) => {
      const lat = row.lat;
      const lng = row.lng;
      if (lat == null || lng == null || !isFiniteLatLng(lat, lng)) {
        missing += 1;
        return;
      }

      const competitionId = String(row.competition);
      built.push({
        id: `${competitionId}-${index}`,
        lat,
        lng,
        competitionId,
        color: getCompetitionMarkerColor(competitionId, competitionIdsInOrder),
      });
    });

    return { markers: built, missingLocationCount: missing };
  }, [checkins, competitionIdsInOrder]);

  return {
    markers,
    isLoading,
    error,
    totalCheckins: checkins.length,
    missingLocationCount,
  };
}
